import GridListViewComponent from "../components/Common/GridListViewComponent";
import { useCallback, useEffect, useState } from "react";
import {
  createBuildings,
  createProperties,
  deleteBuilding,
  deleteProperty,
  getAllProperties,
  getProperty,
  updateBuilding,
  updateProperty,
} from "../lib/api";
import PropertyDetailCard from "../components/Common/PropertyDetailCard";
import AddBuldingModel from "../components/EditUnits/AddBuldingModel";
import ToastMessage from "../components/Common/ToastMessage";
import AddpPropertyModel from "../components/EditUnits/AddpPropertyModel";
import ConfirmModal from "../components/Common/ConfirmModal";
import EditPropertyModel from "../components/Common/EditPropertyModal";
import { useNavigate } from "react-router-dom";
import PropertyList from "../components/PropertyList";
import LoadingModal from "../components/Common/LoadingModal";
import Pagination from "../components/Common/Pagination";
import { useIsWriteRestrictedUser } from "../hooks/useIsWriteRestrictedUser";

const DEFAULT_PAGE_SIZE = 25;
const GRID_VIEW_STORAGE_KEY = "managePropertiesViewMode";

const ManageProperties = () => {
  const isAccessRestricted = useIsWriteRestrictedUser();
  const navigate = useNavigate();
  const getStoredGridView = () => {
    if (window.innerWidth < 1025) return true;
    try {
      const stored = localStorage.getItem(GRID_VIEW_STORAGE_KEY);
      return stored ? stored === "grid" : true;
    } catch (error) {
      return true;
    }
  };
  const [initialPropertyName, setInitialPropertyName] = useState();

  const [properties, setProperties] = useState([]);
  const [buildingList, setBuildingList] = useState([]);
  const [gridView, setGridView] = useState(() => getStoredGridView());
  const [selectedpropertie, setSelectedpropertie] = useState({});
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [showAddPropertyModel, setShowAddPropertyModel] = useState(false);
  const [showEditPropertyModel, setShowEditPropertyModel] = useState(false);
  const [showAddBuildingModel, setShowAddBuildingModel] = useState(false);
  const [showEditBuildingModal, setShowEditBuildingModal] = useState(false);
  const [showDeleteModel, setShowDeleteModel] = useState(false);
  const [showDeleteBuildingModel, setShowDeleteBuildingModel] = useState(false);
  const [buildingToDelete, setBuildingToDelete] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1025) {
        setGridView(true);
        return;
      }
      setGridView(getStoredGridView());
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (window.innerWidth < 1025) return;
    try {
      localStorage.setItem(GRID_VIEW_STORAGE_KEY, gridView ? "grid" : "list");
    } catch (error) {
      // Ignore localStorage write failures (e.g. privacy mode).
    }
  }, [gridView]);

  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success",
  });
  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
  };

  const handleNavigate = () => {
    navigate("/dashboard");
  };

  const getAllPropertiesApi = useCallback(async (append = true) => {
    if (!append) {
      setIsInitialLoading(true);
    }

    const payload = {
      pageNumber: currentPage + 1,
      pageSize: DEFAULT_PAGE_SIZE,
    };

    try {
      const response = await getAllProperties(payload);
      const fetchedProperties = response?.data?.data?.properties || [];
      const total = response?.data?.data?.count || 0;
      setProperties(fetchedProperties);
      setTotalCount(total);
    } catch (error) {
      console.error("Error fetching unit data:", error);
    } finally {
      // setIsLoadingProperties(false);

      setIsInitialLoading(false);
    }
  }, [currentPage]);

  useEffect(() => {
    getAllPropertiesApi(true);
  }, [getAllPropertiesApi]);

  const getPropertyApi = useCallback(async () => {
    if (!selectedpropertie?.unid) return;
    try {
      const response = await getProperty({ unid: selectedpropertie?.unid });
      setBuildingList(response?.data?.data?.property?.buildings);
    } catch (error) {
      console.error("Error fetching property data:", error);
    }
  }, [selectedpropertie?.unid]);

  const AddBuildingApi = async (buildingName) => {
    const postData = { name: buildingName.trim() };
    const payload = { unid: selectedpropertie?.unid };
    try {
      const response = await createBuildings(payload, postData);
      await getPropertyApi();

      if (response.status === 200) {
        setProperties((prev) =>
          prev.map((prop) =>
            prop.unid === selectedpropertie.unid
              ? {
                ...prop,
                buildings: [
                  ...(prop.buildings || []),
                  response?.data?.data?.building,
                ],
              }
              : prop
          )
        );

        setSelectedpropertie((prev) => ({
          ...prev,
          buildings: [
            ...(prev.buildings || []),
            response?.data?.data?.building,
          ],
        }));

        setBuildingList((prev) => [
          ...(prev || []),
          response?.data?.data?.building,
        ]);

        showToast("Building added successfully", "success");
      }
    } catch (error) {
      console.error("Error fetching unit data:", error);
      showToast(error.message, "failed");
    }
  };

  const updateBuildingApi = async (unid, name) => {
    const payload = {
      unid: selectedpropertie?.unid,
      buildingUnid: unid,
    };
    const postdata = { name: name.trim() };

    try {
      const response = await updateBuilding(payload, postdata);
      if (response.status === 200) {
        const propertyResponse = await getProperty({
          unid: selectedpropertie?.unid,
        });
        const updatedBuildings =
          propertyResponse?.data?.data?.property?.buildings || [];

        setBuildingList(updatedBuildings);
        setSelectedpropertie((prev) => ({
          ...prev,
          buildings: updatedBuildings,
        }));
        setProperties((prev) =>
          prev.map((prop) =>
            prop.unid === selectedpropertie.unid
              ? { ...prop, buildings: updatedBuildings }
              : prop
          )
        );

        showToast("Building updated successfully", "success");
      }
    } catch (error) {
      showToast(error.message, "failed");
    }
  };

  const deleteBuildingApi = async (buildingUnid) => {
    const payload = {
      unid: selectedpropertie?.unid,
      buildingUnid,
    };
    try {
      const response = await deleteBuilding(payload);

      if (response.status === 200) {
        setProperties((prev) =>
          prev.map((prop) =>
            prop.unid === selectedpropertie.unid
              ? {
                ...prop,
                buildings: (prop.buildings || []).filter(
                  (b) => b.unid !== buildingUnid
                ),
              }
              : prop
          )
        );
        setSelectedpropertie((prev) => ({
          ...prev,
          buildings: (prev.buildings || []).filter(
            (b) => b.unid !== buildingUnid
          ),
        }));
        setBuildingList((prev) =>
          (prev || []).filter((b) => b.unid !== buildingUnid)
        );
        showToast("Building deleted successfully", "success");
      } else {
        showToast("Failed to delete building", "failed");
      }
    } catch (error) {
      console.error("Error fetching unit data:", error);
      if (error?.errorCode === "FMS_BUILDING_REFERENCED_BY_UNITS") {
        showToast(
          error.message ||
          "building cannot be deleted because it is referenced by units.",
          "failed"
        );
      } else {
        showToast("Error: Unable to delete property", "failed");
      }
    }
  };

  const deletePropertyApi = async (propertyUnid) => {
    const payload = { unid: propertyUnid };

    try {
      const response = await deleteProperty(payload);

      if (response.status === 200) {
        setProperties([]);
        getAllPropertiesApi(false, 1);
        setSelectedpropertie(null);
        setShowDeleteModel(false);
        showToast("Property has been deleted successfully.", "success");
      } else {
        showToast("Failed to delete property", "failed");
        setShowDeleteModel(false);
      }
    } catch (error) {
      if (error?.errorCode === "FMS_PROPERTY_REFERENCED_BY_UNITS") {
        showToast(
          "Property cannot be deleted because it is referenced by units.",
          "failed"
        );
      } else {
        showToast("Error: Unable to delete property", "failed");
      }
      setShowDeleteModel(false);
    }
  };

  function handleDeleteProperty() {
    if (selectedpropertie?.unid) {
      deletePropertyApi(selectedpropertie.unid);
    } else {
      showToast("No property selected to delete", "failed");
    }
  }

  function handleDeleteBuilding() {
    if (buildingToDelete) {
      deleteBuildingApi(buildingToDelete);
    } else {
      showToast("No building selected to delete", "failed");
    }
    setShowDeleteBuildingModel(false);
    setBuildingToDelete(null);
  }

  function handleCancelDeleteBuilding() {
    setShowDeleteBuildingModel(false);
    setBuildingToDelete(null);
  }

  const handleCreatePropertyClick = () => {
    setShowAddPropertyModel(true);
  };

  const handleSavePropety = async (propertyName) => {
    try {
      const response = await createProperties({ name: propertyName.trim() });
      const resProperty = response?.data?.data?.property;
      if (response.status === 200) {
        showToast("Property added successfully", "success");
      }
      setSelectedpropertie({
        unid: resProperty?.unid,
        name: resProperty?.name,
      });
      setProperties([]);
      getAllPropertiesApi(false, 1);
      setShowAddPropertyModel(false);
    } catch (error) {
      console.error("Error fetching unit data:", error);
      if (error.status === 409) {
        showToast(error.message, "failed");
      }
    }
  };

  const handleSaveEditedProperty = async (updatedName) => {
    try {
      const payload = {
        unid: selectedpropertie?.unid,
      };

      const postData = {
        name: updatedName.trim(),
      };

      const response = await updateProperty(payload, postData);
      if (response.status === 200) {

        setProperties([]);
        getAllPropertiesApi(false, 1);

        setSelectedpropertie((prev) => ({ ...prev, name: updatedName }));
        setShowEditPropertyModel(false);
        showToast("Property name updated successfully.", "success");
      } else {
        showToast("Failed to update property name", "failed");
      }
    } catch (error) {
      if (error.status === 409) {
        showToast(error.message, "failed");
      } else {
        showToast("Error : Failed to update property name", "failed");
      }
    }
  };

  const handleCancel = () => {
    setShowDeleteModel(false);
    setSelectedpropertie(null);
  };

  if (isInitialLoading) {
    return <LoadingModal />;
  }

  const pageCount = Math.ceil(totalCount / DEFAULT_PAGE_SIZE);

  const handlePageClick = ({ selected }) => {
    setCurrentPage(selected);
  };

  return (
    <>
      <div className="titleAndFilters" data-testid="manageproperties-title-and-filters">
        <div className="flex flex-col md:flex-row gap-3 justify-between items-start md:items-center w-full">
          <div className="flex gap-4 items-center justify-between w-full sm:w-auto sm:justify-normal max-[380px]:flex-col max-[380px]:gap-2 max-[380px]:items-start">
            <h2 className="mainTitle text-[#000000DE] dark:text-white font-semibold text-xl leading-5 lg:text-2xl lg:leading-6 tracking-[0.036px]" data-testid="manageproperties-title">
              Manage Properties {totalCount > 0 && (
                <span data-testid="manageproperties-title-count">({totalCount})</span>)}
            </h2>
            <span className="text-xl leading-5 lg:text-2xl font-normal text-[#AFC3BC] dark:text-[#AFC3BCCC] lg:leading-6 tracking-[0.036px] hidden sm:block">
              |
            </span>
            <h2
              className="mainTitle text-[#FF8C65] font-semibold text-md leading-5 lg:text-base lg:leading-6 tracking-[0.036px] cursor-pointer underline"
              onClick={handleNavigate}
              data-testid="manageproperties-goto-dashboard-button"
            >
              Go to Dashboard
            </h2>
          </div>

          <div className="searchAndFilter flex items-center justify-between gap-x-4 gap-y-2 flex-wrap flex-row-reverse md:flex-row md:justify-items-end w-full md:w-auto">
            <div className="flex items-center gap-4">
              {/* <SearchComponent /> */}
              <div className="hidden lg:block">
                <GridListViewComponent
                  gridView={gridView}
                  setGridView={setGridView}
                  data-testid="manageproperties-grid-list-view-toggle"
                />
              </div>
            </div>
            <button
              className={`bg-theme text-white rounded-md text-sm leading-4 lg:text-base lg:leading-5 font-semibold tracking-[-0.32px] px-5 py-1 flex items-center justify-center h-9 shrink-0 grow-0 hover:bg-theme/80 ${
                isAccessRestricted ? "opacity-40 cursor-not-allowed" : ""
              }`}
              onClick={() => {
                if (isAccessRestricted) return;
                handleCreatePropertyClick();
              }}
              disabled={isAccessRestricted}
              data-testid="manageproperties-create-property-button"
            >
              Create Property
            </button>
          </div>
        </div>
      </div>

      {properties?.length > 0 && (
        <>
          {gridView ? (
              <div className="mt-4 propertyHolder">
                <div className="grid grid-cols-[repeat(auto-fill,minmax(312px,1fr))] max-[380px]:grid-cols-1 gap-6">
                  {properties.map((eachProperty) => {
                    return (
                      <PropertyDetailCard
                        key={eachProperty?.unid}
                        property={eachProperty}
                        setSelectedpropertie={setSelectedpropertie}
                        setShowAddBuildingModel={setShowAddBuildingModel}
                        setShowDeleteModel={setShowDeleteModel}
                        setShowEditPropertyModel={setShowEditPropertyModel}
                        setShowEditBuildingModal={setShowEditBuildingModal}
                        setInitialPropertyName={setInitialPropertyName}
                        isActionsDisabled={isAccessRestricted}
                      />
                    );
                  })}
                </div>
              </div>
          ) : (
            <PropertyList
              properties={properties}
              setSelectedpropertie={setSelectedpropertie}
              setShowAddBuildingModel={setShowAddBuildingModel}
              setShowDeleteModel={setShowDeleteModel}
              setShowEditPropertyModel={setShowEditPropertyModel}
              setShowEditBuildingModal={setShowEditBuildingModal}
              setInitialPropertyName={setInitialPropertyName}
              isActionsDisabled={isAccessRestricted}
            />
          )}
          {/* Pagination */}
          {totalCount > DEFAULT_PAGE_SIZE && (
            <Pagination pageCount={pageCount} onPageChange={handlePageClick} currentPage={currentPage}/>
          )}
        </>
      )}

      {/* Loading and when no zones data available */}
      {isInitialLoading ?
        <div className="flex justify-center mt-4 text-gray-500 text-sm" data-testid="manageproperties-loading-text">
          Loading More properties...
        </div>
        : properties?.length === 0 && <div data-testid="manageproperties-empty-state">
          <p className="text-center dark:text-white text-dark-gray mt-4">
            No property available
          </p>
        </div>
      }

      {showEditBuildingModal && (
        <>
          <AddBuldingModel
            isEditMode={true}
            setShowAddBuildingModel={setShowEditBuildingModal}
            buildingList={selectedpropertie.buildings || []}
            selectedpropertie={selectedpropertie}
            AddBuildingApi={AddBuildingApi}
            deleteBuildingApi={deleteBuildingApi}
            updateBuildingApi={updateBuildingApi}
            setShowDeleteBuildingModel={setShowDeleteBuildingModel}
            setBuildingToDelete={setBuildingToDelete}
          />
        </>
      )}

      {showAddBuildingModel && (
        <AddBuldingModel
          setShowAddBuildingModel={setShowAddBuildingModel}
          buildingList={selectedpropertie.buildings || []}
          selectedpropertie={selectedpropertie}
          AddBuildingApi={AddBuildingApi}
          deleteBuildingApi={deleteBuildingApi}
          updateBuildingApi={updateBuildingApi}
          setShowDeleteBuildingModel={setShowDeleteBuildingModel}
          setBuildingToDelete={setBuildingToDelete}
          allBuildings={buildingList}
        />
      )}

      {showAddPropertyModel && (
        <AddpPropertyModel
          setShowAddPropertyModel={setShowAddPropertyModel}
          onSubmit={handleSavePropety}
        />
      )}

      {showEditPropertyModel && (
        <EditPropertyModel
          setShowEditPropertyModel={setShowEditPropertyModel}
          onSubmit={handleSaveEditedProperty}
          initialPropertyName={initialPropertyName}
        />
      )}

      {showDeleteBuildingModel && (
        <ConfirmModal
          title="Remove Building"
          subText="Your Building will be deleted from this property"
          confirmMessage="Are you sure you want to delete the building from property?"
          cancelText="Cancel"
          submitText="Confirm"
          onCancel={handleCancelDeleteBuilding}
          onSubmit={handleDeleteBuilding}
        />
      )}

      {showDeleteModel && (
        <ConfirmModal
          title="Delete Property"
          subText="Your property will be deleted."
          confirmMessage="Are you sure you want to delete this property?"
          cancelText="Cancel"
          submitText="Confirm"
          onCancel={handleCancel}
          onSubmit={handleDeleteProperty}
        />
      )}

      <ToastMessage
        message={toast.message}
        type={toast.type}
        show={toast.show}
        onClose={() => setToast((prev) => ({ ...prev, show: false }))}
      />
    </>
  );
};

export default ManageProperties;
